import 'src/styles/piece-landing.css';

import { faQrcode, faShareFromSquare, faWrench } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from 'src/components/header';
import Loader from 'src/components/loader';
import NotFound from 'src/components/not-found';
import Button from 'src/components/ui/Button';
import Checkbox from 'src/components/ui/Checkbox';
import { api } from 'src/utils/api';
import { API_RESOURCES } from 'src/utils/constants';
import {
  getPieceOverviewPath,
  getPieceScanViewPath,
  getPieceScanViewPreference,
  isFinishedPieceStatus,
  PIECE_SCAN_VIEW_OPTIONS,
  setPieceScanViewPreference,
} from 'src/utils/pieceScanViewPreference';
import { getUuid } from 'src/utils/url';
import userPropType from 'src/utils/user-prop-type';

const VIEW_OPTION_CONFIG = {
  [PIECE_SCAN_VIEW_OPTIONS.RUN]: {
    id: PIECE_SCAN_VIEW_OPTIONS.RUN,
    title: 'Current Run',
    desc: 'Tap to view current manufacturing process and material tracking.',
    icon: faShareFromSquare,
    variant: 'run',
    actionLabel: 'Open Current Run',
  },
  [PIECE_SCAN_VIEW_OPTIONS.PIECE]: {
    id: PIECE_SCAN_VIEW_OPTIONS.PIECE,
    title: 'Piece Page',
    desc: 'Tap to view detailed specifications, status, and 3D view.',
    icon: faWrench,
    variant: 'piece',
    actionLabel: 'Open Piece Page',
  },
};

const ViewOptionCard = ({ option, active, onSelect }) => (
  <div
    className={classNames('view-option-card', `view-option-card--${option.variant}`, {
      'view-option-card--selected': active,
    })}
    role='button'
    tabIndex={0}
    onClick={() => onSelect(option.id)}
    onKeyDown={event => {
      if (event.key === 'Enter' || event.key === ' ') onSelect(option.id);
    }}
  >
    <div
      className={classNames(
        'view-option-card__header',
        `view-option-card__header--${option.variant}`
      )}
    >
      <FontAwesomeIcon icon={option.icon} className='view-option-card__icon' />
      <span>{option.title}</span>
    </div>
    <div className='view-option-card__body'>
      <span className='view-option-card__desc'>{option.desc}</span>
      <span
        className='view-option-card__checkbox'
        onClick={event => event.stopPropagation()}
        onKeyDown={event => event.stopPropagation()}
      >
        <Checkbox
          id={`view-option-${option.id}`}
          checked={active}
          onChange={() => onSelect(option.id)}
        />
      </span>
    </div>
  </div>
);

ViewOptionCard.propTypes = {
  option: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    desc: PropTypes.string.isRequired,
    icon: PropTypes.object.isRequired,
    variant: PropTypes.string.isRequired,
    actionLabel: PropTypes.string.isRequired,
  }).isRequired,
  active: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
};

export const PieceLandingPage = ({ user }) => {
  const { print: printUuid } = useParams();
  const navigate = useNavigate();
  const [selectedView, setSelectedView] = useState(PIECE_SCAN_VIEW_OPTIONS.PIECE);
  const [rememberSelection, setRememberSelection] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pieceUuid, setPieceUuid] = useState(null);
  const [runUuid, setRunUuid] = useState(null);

  useEffect(() => {
    const savedPreference = getPieceScanViewPreference(user);
    setSelectedView(savedPreference.view);
    setRememberSelection(savedPreference.remember);
  }, [user]);

  useEffect(() => {
    const fetchPrint = async () => {
      try {
        const print = await api.get(`${API_RESOURCES.PRINT}/${printUuid}/`).json();
        const nextPieceUuid = print.piece ? getUuid(print.piece) : null;
        const nextRunUuid = print.run ? getUuid(print.run) : null;

        if (!nextPieceUuid) {
          setError('Failed to find piece.');
          return;
        }

        const piece = await api.get(`${API_RESOURCES.PIECE}/${nextPieceUuid}/`).json();

        if (isFinishedPieceStatus(piece.status) || !nextRunUuid) {
          navigate(getPieceOverviewPath(nextPieceUuid), { replace: true });
          return;
        }

        const preference = getPieceScanViewPreference(user);
        const rememberedPath =
          preference.remember &&
          getPieceScanViewPath({
            view: preference.view,
            pieceUuid: nextPieceUuid,
            runUuid: nextRunUuid,
          });

        if (rememberedPath) {
          navigate(rememberedPath, { replace: true });
          return;
        }

        setPieceUuid(nextPieceUuid);
        setRunUuid(nextRunUuid);
      } catch (fetchError) {
        if (fetchError.name === 'HTTPError' && fetchError.response?.status === 404) {
          setError('Failed to find print.');
        } else {
          setError(fetchError);
        }
      } finally {
        setIsLoading(false);
      }
    };

    void fetchPrint();
  }, [printUuid, navigate, user]);

  const handleOpen = () => {
    setPieceScanViewPreference(user, {
      remember: rememberSelection,
      view: selectedView,
    });

    const path = getPieceScanViewPath({
      view: selectedView,
      pieceUuid,
      runUuid,
    });

    if (!path) {
      setError('Failed to find run.');
      return;
    }

    navigate(path, { replace: true });
  };

  const selectedOption = VIEW_OPTION_CONFIG[selectedView];

  if (error) {
    return (
      <>
        <Header title='Select View' user={user} />
        <NotFound id={printUuid} text={typeof error === 'string' ? error : 'Failed to load print.'} />
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <Header title='Select View' user={user} />
        <main role='main' className='text-center'>
          <Loader inline text='data' />
        </main>
      </>
    );
  }

  return (
    <>
      <Header title='Select View' user={user} />
      <main role='main' className='text-center piece-landing'>
        <p className='piece-landing__prompt'>Do you want to view:</p>

        <div className='piece-landing__options'>
          <ViewOptionCard
            option={VIEW_OPTION_CONFIG[PIECE_SCAN_VIEW_OPTIONS.RUN]}
            active={selectedView === PIECE_SCAN_VIEW_OPTIONS.RUN}
            onSelect={setSelectedView}
          />

          <div className='separator piece-landing__separator'>or</div>

          <ViewOptionCard
            option={VIEW_OPTION_CONFIG[PIECE_SCAN_VIEW_OPTIONS.PIECE]}
            active={selectedView === PIECE_SCAN_VIEW_OPTIONS.PIECE}
            onSelect={setSelectedView}
          />
        </div>

        <div className='piece-landing__remember'>
          <Checkbox
            id='piece-landing-remember'
            label='Remember my selection for future scans.'
            checked={rememberSelection}
            onChange={event => setRememberSelection(event.target.checked)}
          />
        </div>

        <div className='piece-landing__actions'>
          <Button
            block
            fill
            variant='primary'
            icon={<FontAwesomeIcon icon={faQrcode} />}
            onClick={handleOpen}
          >
            {selectedOption.actionLabel}
          </Button>
          <Button block variant='default' onClick={() => navigate(-1)}>
            Cancel
          </Button>
        </div>
      </main>
    </>
  );
};

PieceLandingPage.propTypes = {
  user: userPropType,
};

PieceLandingPage.defaultProps = {
  user: null,
};
